import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Tooltip,
    Dialog,
    IconButton,
    Radio,
    RadioGroup,
    FormControl,
    FormControlLabel,
    LinearProgress,
    Tabs,
    Tab,
    Modal,
} from "@mui/material";
import { Pie, Line } from "react-chartjs-2";
import "chart.js/auto";
import { AddCircle, RemoveCircle, InsertChart } from "@mui/icons-material";
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'; // Coin icon

import { addMeal, getMeal, deleteMeal } from "../../utils/api";
import { fetchData, foodOptions } from "../../utils/fetchData";
import { debounce } from 'lodash'; // Install lodash if not already installed
import Loader from "../Loader";
import { getGoal, addGoal } from "../../utils/api"; // Assuming these API calls are correctly exported
import CaloricGoal from './CaloricGoal'
const FoodTrack = ({ userId }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [chartData, setChartData] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);
    const [mealTypeDialogOpen, setMealTypeDialogOpen] = useState(false);
    const [selectedFood, setSelectedFood] = useState(null);
    const [mealDetails, setMealDetails] = useState({}); // State for meals grouped by type
    const [nutrientGoals, setNutrientGoals] = useState({ protein: 0, carbs: 0, fats: 0 });
    const [tabIndex, setTabIndex] = useState(0);
    const [caloricGoals, setcaloricGoals] = useState({ calories: 0, protein: 0, carbs: 0, fats: 0 });
    const [timePeriod, setTimePeriod] = useState('daily'); // Default to daily view

    const [showPopup, setShowPopup] = useState(false);//popup

    // New state for serving size editing
    const [servingSizeDialogOpen, setServingSizeDialogOpen] = useState(false);
    const [servingSizeValue, setServingSizeValue] = useState(""); // User-entered value (number)
    const [servingSizeType, setServingSizeType] = useState("grams"); // "grams" or "percentage"

    const nutrientGoalValues = {
        "Lutein + zeaxanthin": 8,
        "Vitamin E (alpha-tocopherol)": 15,
        "Niacin": 15,
        "Pantothenic acid": 5,
        "Vitamin B-6": 1.65,
        "Fatty acids, total monounsaturated": 15,
        "Fatty acids, total polyunsaturated": 11,
        "Isoleucine": 0.88,
        "Leucine": 1.6,
        "Lysine": 1.5,
        "Cystine": 0.2,
        "Valine": 0.85,
        "Arginine": 1.5,
        "Histidine": 0.2,
        "Aspartic acid": 1.0,
        "Serine": 0.7,
        "SFA 16:0": 2.5,
        "SFA 18:0": 3.5,
        "SFA 20:0": 0,
        "MUFA 18:1": 12,
        "PUFA 18:2": 5.25,
        "PUFA 18:3": 1.35,
        "Total lipid (fat)": caloricGoals["fats"],
        "Water": 3.2,
        "Calcium, Ca": 1050,
        "Potassium, K": 4100,
        "Zinc, Zn": 9.5,
        "Selenium, Se": 55,
        "Copper, Cu": 0.9,
        "Manganese, Mn": 2.45,
        "Vitamin K (phylloquinone)": 105,
        "Vitamin C, total ascorbic acid": 82.5,
        "Thiamin": 1.15,
        "Riboflavin": 1.2,
        "Folate, total": 400,
        "Fiber, total dietary": 31.5,
        "Iron, Fe": 13,
        "Magnesium, Mg": 365,
        "Phosphorus, P": 700,
        "Tryptophan": 0.35,
        "Threonine": 0.6,
        "Methionine": 0.45,
        "Phenylalanine": 0.8,
        "Tyrosine": 0.5,
        "Alanine": 0.7,
        "Glutamic acid": 2.0,
        "Glycine": 0.7,
        "Proline": 0.7,
        "Fatty acids, total saturated": 22,
        "SFA 8:0": 0,
        "SFA 12:0": 0.3,
        "SFA 14:0": 0.3,
        "Protein": caloricGoals["protein"],
        "Ash": 2.25,
        "Starch": 35,
        "Betaine": 96.7,
        "Choline, total": 487,
        "Folate, food": 400,
        "Sodium, Na": 1900,
        "Folate, DFE": 400,
        "Vitamin B-12": 2.4,
        "Carbohydrate, by difference": caloricGoals["carbs"],
        "Energy": caloricGoals["calories"],
        "Glucose": 0.75,
        "Maltose": 2,
        "Total Sugars": 27.5,
        "Folic acid": 400,
        "Vitamin K (Dihydrophylloquinone)": 0,
        "Fructose": 0.75,
    };
    const foodMap = {
        "חזה עוף": "Chicken breast",
        "בשר רזה": "Lean beef",
        "דג": "Fish",
        "טונה": "Tuna",
        "ביצה": "Egg",
        "חלבון ביצה": "Egg white",
        "קוטג' 5%": "Cottage cheese 5%",
        "יוגורט יווני": "Greek yogurt",
        "חומוס": "Hummus",
        "אבוקדו": "Avocado",
        "תפוח אדמה": "Potato",
        "אורז": "Rice",
        "פסטה": "Pasta",
        "קטניות": "Legumes",
        "סלמון": "Salmon",
        "קינואה": "Quinoa",
        "במיה": "Okra",
        "ברוקולי": "Broccoli",
        "תרד": "Spinach",
        "גזר": "Carrot",
        "טוסט מחיטה מלאה": "Whole wheat toast",
        "גרעיני חמנייה": "Sunflower seeds",
        "אגוזים": "Nuts",
        "שקדים": "Almonds",
        "תמרים": "Dates",
        "בננה": "Banana",
        "תפוח": "Apple",
        "פירות יער": "Berries",
        "קיווי": "Kiwi",
        "מיץ תפוזים": "Orange juice",
        "מים": "Water",
        "חלב סויה": "Soy milk",
        "חלב": "Milk",
        "שייק חלבון": "Protein shake",
        "משקה איזוטוני": "Electrolyte drink",
        "גרנולה": "Granola",
        "סוכר חום": "Brown sugar",
        "שוקולד מריר": "Dark chocolate",
        "קפה": "Coffee",
        "תה ירוק": "Green tea",
        "תפוז": "Orange",
        "פירות טריים": "Fresh fruits",
        "חמאת בוטנים": "Peanut butter",
        "טחינה": "Tahini",
        "בייגל": "Bagel",
        "מאפינס דל קלוריות": "Low-calorie muffin",
        "קפיר": "Kefir",
        "סויה": "Soy",
        "חלב קוקוס": "Coconut milk",
        "חומוס קל": "Light hummus",
        "תפוח אדמה מתוק": "Sweet potato",
        "חציל": "Eggplant",
        "זיתים": "Olives",
        "סלק": "Beetroot",
        "כרובית": "Cauliflower",
        "פול": "Fava beans",
        "חסה": "Lettuce",
        "כרוב": "Cabbage",
        "קולורבי": "Kohlrabi",
        "פפאיה": "Papaya",
        "גויאבה": "Guava",
        "אבטיח": "Watermelon",
        "מנגו": "Mango",
        "מלון": "Melon",
        "פירות הדר": "Citrus fruits",
        "תפוח אדמה אמצעי": "Medium potato",
        "סלמון טרי": "Fresh salmon",
        "חלבון קונסנטרט": "Whey protein concentrate",
        "חלבון בידוד": "Whey protein isolate",
        "אורז חום": "Brown rice",
        "ממרח שוקולד": "Chocolate spread",
        "אבטיח דחוס": "Compressed watermelon",
        "ירקות מאודים": "Steamed vegetables",
        "רוטב סויה": "Soy sauce",
        "חמאת שקדים": "Almond butter",
        "קפאין": "Caffeine",
        "גרגרי חומוס": "Chickpeas",
        "שמנת חמוצה דלת שומן": "Low-fat sour cream",
        "יוגורט דל שומן": "Low-fat yogurt",
        "פיצוחים": "Nuts and seeds",
        "רוטב חומץ בלסמי": "Balsamic vinegar",
        "חמאת גהי": "Ghee butter",
        "סוכר קוקוס": "Coconut sugar",
        "אורז בר": "Wild rice",
        "אורז יפני": "Japanese rice",
        "מאפים ללא גלוטן": "Gluten-free baked goods",
        "שעועית ירוקה": "Green beans",
        "תפוחים ירוקים": "Green apples",
        "חצי אבוקדו": "Half avocado",
        "קישוא": "Zucchini",
        "דלעת": "Pumpkin",
        "פטריות": "Mushrooms",
        "קינואה מבושלת": "Cooked quinoa",
        "דגי סלמון": "Salmon fillets",
        "גזר גמדי": "Baby carrots",
        "פירות מעורבים": "Mixed fruits",
        "קינואה חום": "Brown quinoa",
        "רסק תפוחים": "Applesauce",
        "סלט ירקות": "Vegetable salad",
        "סלט טונה": "Tuna salad",
        "טופו": "Tofu",
        "טופו גרגרי": "Tofu cubes",
        "קינמון": "Cinnamon",
        "תמצית וניל": "Vanilla extract",
        "אבקת חלבון טבעונית": "Vegan protein powder",
        "פירות מיובשים": "Dried fruits",
        "פודינג חלבוני": "Protein pudding",
        "פירות טרופיים": "Tropical fruits",
        "קרם קוקוס": "Coconut cream",
        "שוקולד חלב": "Milk chocolate",
        "חלב שיבולת שועל": "Oat milk",
        "פופקורן דל קלוריות": "Low-calorie popcorn",
        "קקאו": "Cocoa",
        "תפוז סיני": "Mandarin",
        "שיבולת שועל": "Oats",
        "פול ירוק": "Green peas",
        "אטריות אורז": "Rice noodles",
        "קוקוס טחון": "Shredded coconut",
        "חלב מרוכז": "Condensed milk",
        "שקדים קלויים": "Roasted almonds",
        "טחינת שומשום": "Sesame tahini",
        "ממרח אגוזי לוז": "Hazelnut spread",
        "שייק ירוק": "Green smoothie",
        "מוזלי": "Muesli",
        "סלט קייל": "Kale salad",
        "כרובית צלויה": "Roasted cauliflower",
        "ממרח עגבניות": "Tomato spread",
        "חמאת קקאו": "Cocoa butter",
        "אורז לבן": "White rice",
        "תפוזי דם": "Blood oranges",
        "קוקוס מיובש": "Dried coconut",
        "קוקטייל פירות": "Fruit cocktail",
        "פולקות": "Polenta",
        "טחינת חומוס": "Chickpea tahini",
        "פסקה": "Flaxseed",
        "גרגרי תירס": "Corn kernels",
        "שומשום": "Sesame",
        "תפוז סיני קטן": "Small mandarin orange",
        "טחינה לא ממותקת": "Unsweetened tahini",
        "תפוז בגודל בינוני": "Medium orange",
        "דלעת אפויה": "Baked pumpkin",
        "פסטה מחיטה מלאה": "Whole wheat pasta",
        "אטריות ביצים": "Egg noodles",
        "סלט תפוח אדמה": "Potato salad",
        "סלט תוצרת בית": "Homemade salad",
        "חומוס על טוסט": "Hummus on toast",
        "פסטה ללא גלוטן": "Gluten-free pasta",
        "סלט חומוס": "Chickpea salad",
        "אורז פרא": "Basmati rice",
        "פסטה ירוקה": "Green pasta",
        "רוטב עגבניות": "Tomato sauce",
        "חומוס גולמי": "Raw hummus",
        "אורז סושי": "Sushi rice",
        "חומוס גרגרי": "Whole chickpeas",
        "חמאת מקדמיה": "Macadamia butter",
        "שוקולד מריר עם אגוזים": "Dark chocolate with nuts",
        "חמאת קוקוס": "Coconut butter",
        "סלט טאבולה": "Tabbouleh salad",
        "פריזבי": "Frisbee (grilled vegetables)",
        "אורז אדום": "Red rice",
        "קוקטייל ירקות": "Vegetable cocktail",
        "ירקות קלויים": "Roasted vegetables",
        "שניצל עוף": "Chicken schnitzel",
        "כדורי חומוס": "Hummus balls",
        "פוקצ'יה": "Focaccia bread",
        "פלאפל": "Falafel",
        "הום פרייז": "Home fries",
        "פסטה עם טחינה": "Pasta with tahini",
        "סלט כוסברה": "Coriander salad",
        "אפונה ירוקה": "Green peas",
        "פשטידת ירקות": "Vegetable frittata",
        "פרי פטל": "Raspberry",
        "שמנת פריזר": "Frozen cream",
        "חמאת צמחונית": "Vegetarian butter",
        "קנלוני": "Cannelloni",
        "טחינת חומוס קל": "Light chickpea tahini",
        "תירס חם": "Hot corn",
        "עוגת גזר": "Carrot cake",
        "עוגת תפוחים": "Apple cake",
        "בצל ירוק": "Spring onion",
        "צ'ילי ירוק": "Green chili",
        "רוטב פסטו": "Pesto sauce",
        "סלט סלק": "Beetroot salad",
        "לחם שיפון": "Rye bread",
        "חמאה תירס": "Corn butter",
        "בצל קצוץ": "Chopped onion",
        "פיתות": "Pita bread",
        "ריבת תות": "Strawberry jam",
        "קיש": "Quiche",
        "פופקורן בטעמים": "Flavored popcorn",
        "הוגו": "Hugo (mint drink)",
        "מרק ירקות": "Vegetable soup",
        "מרק עדשים": "Lentil soup",
        "פריזבי עם ירקות": "Frisbee with vegetables",
        "כוסמת": "Buckwheat",
        "קולפן": "Peeler",
        "תבשיל תפוחי אדמה": "Potato stew",
        "סלט חומוס עם ירקות": "Chickpea salad with vegetables",
        "בירה דלת אלכוהול": "Low-alcohol beer",
        "מרק עגבניות": "Tomato soup",
        "סושי ירקות": "Vegetable sushi",
        "גלידה יוגורט": "Frozen yogurt",
        "פסטה עם פטריות": "Pasta with mushrooms",
        "קולפן ירקות": "Vegetable peeler",
        "פסטה עם גבינה": "Pasta with cheese",
        "קינמון ושוקולד": "Cinnamon and chocolate",
        "סלט אבוקדו": "Avocado salad",
        "עוגת דלעת": "Pumpkin pie",
        "קישואים קלויים": "Roasted zucchini",
        "רוטב גבינה": "Cheese sauce",
        "פירות טרופיים טריים": "Fresh tropical fruits",
        "שמנת דלת שומן": "Low-fat cream",
        "פאייה": "Paella",
        "סלט סלק עם גבינה": "Beetroot salad with cheese",
        "פשטידת קישואים": "Zucchini frittata",
        "קיש עם תרד": "Spinach quiche",
        "סלט תפוחים עם גבינה": "Apple salad with cheese",
        "באגט": "Baguette",
        "פסטה עם עגבניות": "Pasta with tomatoes",
        "פרגית": "Grilled chicken thighs",
        "תפוח אדמה מרוסק": "Mashed potatoes",
        "קינואה עם ירקות": "Quinoa with vegetables",
        "פסטה פריזאי": "Frisée pasta",
        "שוקולד חלב עם אגוזים": "Milk chocolate with nuts",
        "פשטידת תירס": "Corn fritters",
        "קוסקוס": "Couscous",
        "טוסט עם גבינה": "Toast with cheese",
        "אורז פרסי": "Persian rice",
        "לחם חלב": "Milk bread",
        "קונפי שום": "Garlic confit",
        "רוטב שמנת": "Cream sauce",
        "סלט חציל": "Eggplant salad",
        "קציצות בשר": "Meatballs",
        "חמאת חלב": "Dairy butter",
        "חלבונים בתבשיל": "Egg whites in stew",
        "פשטידת חציל": "Eggplant frittata",
        "פלאפל ירקות": "Vegetable falafel",
        "פסטה בולונז": "Bolognese pasta",
        "תפוח אדמה אפוי": "Baked potato",
        "קינואה עם פירות": "Quinoa with fruits",
        "ממרח אגוזי מקדמיה": "Macadamia nut spread",
        "סלט טונה עם ירקות": "Tuna salad with vegetables",
        "פנקייק ירוק": "Green pancake",
        "סלט פריזאי": "Frisée salad",
        "מנת סושי": "Sushi plate",
        "פסטה אדומה": "Red pasta",
        "סלט גבינות": "Cheese salad",
        "פריזי דובדבן": "Cherry frisbee",
        "פסטה עם שמנת": "Pasta with cream",
        "פשטידת דג": "Fish fritters",
        "פסטה עוף": "Chicken pasta",
        "עוגת פרג": "Poppy seed cake",
        "אורז סושי דמוי": "Sushi-style rice",
        "מרק ירקות עם קוסקוס": "Vegetable soup with couscous",
        "סלט טונה עם קישואים": "Tuna salad with zucchini",
        "תפוחים ירוקים": "Green apples",
        "פשטידת פרג": "Poppy seed frittata",
        "מרק עגבניות מרוסק": "Crushed tomato soup",
        "פסטה איטלקית": "Italian pasta",
        "עוגת חלווה": "Halva cake",
        "קיש מתוק": "Sweet quiche",
        "סלט מרק עם סלט": "Soup salad with salad",
        "קינואה עם גבינה": "Quinoa with cheese",
        "קינואה חמה": "Hot quinoa",
        "קוסקוס עם ירקות": "Couscous with vegetables",
        "עוגת פריז": "Paris cake",
        "שוקולד חלב שוקולד": "Milk chocolate with chocolate",
        "פסטה עם גזר": "Pasta with carrots",
        "פסטה בסלט": "Pasta salad",
        "פלאפל עשוי": "Prepared falafel",
        "קולפני תפוחי אדמה": "Potato peelers",
        "הוגו טרי": "Fresh Hugo",
        "קינמון חום": "Brown cinnamon",
        "תירס עם חמאה": "Corn with butter",
        "טפנד זיתים": "Olive tapenade",
        "קרפצ'יו": "Carpaccio",
        "קינואה קרה": "Cold quinoa",
        "תפוזים סיניים": "Chinese oranges",
        "מוס שוקולד": "Chocolate mousse",
        "רוטב פריזאי": "Frisée sauce",
        "עוף צלוי": "Roasted chicken",
        "קציצות טופו": "Tofu meatballs",
        "קוקוס עם שמנת": "Coconut with cream",
        "קוקוס חלב": "Coconut milk",
        "קוקוס קינמון": "Coconut cinnamon",


    }
    const mealTypes = {
        Breakfast: "ארוחת בוקר",
        Lunch: "ארוחת צהריים",
        Dinner: "ארוחת ערב",
        Snack: "נשנושים"
    };
    // State for checking and opening CaloricGoal modal
    const [goal, setGoal] = useState(null);
    const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
    // Callback function to update the caloric goal
    const updateCaloricGoal = (caloricGoal) => {
        setcaloricGoals({
            calories: parseFloat(caloricGoal.calories) || 0,  // Convert string to number
            protein: parseFloat(caloricGoal.proteins) || 0,  // Convert string to number
            carbs: parseFloat(caloricGoal.carbs) || 0,      // Convert string to number
            fats: parseFloat(caloricGoal.fats) || 0,        // Convert string to number
        });
    };
    // Fetch user's goal on mount
    useEffect(() => {
        const fetchUserGoal = async () => {
            try {
                const userGoal = await getGoal(userId);
                // If `userGoal` is an array, sum the values for each property
                let totalCalories = 0;
                let totalProtein = 0;
                let totalCarbs = 0;
                let totalFats = 0;

                userGoal.forEach(entry => {
                    totalCalories += Number(entry.calories);
                    totalProtein += Number(entry.proteins);
                    totalCarbs += Number(entry.carbs);
                    totalFats += Number(entry.fats);
                });

                // Update the state with the aggregated values
                setcaloricGoals({
                    calories: totalCalories,
                    protein: totalProtein,
                    carbs: totalCarbs,
                    fats: totalFats,
                });

                if (userGoal == '') {
                    setIsGoalModalOpen(true); // Open the modal if no goal is set

                } else {
                    setGoal(userGoal);
                }

            } catch (error) {
                console.error("Error fetching user's goal:", error);
            }
        };

        fetchUserGoal();
    }, [userId]);


    const handleSearchDebounced = debounce(async (term) => {
        if (!term.trim()) {
            // If the search term is empty, clear the search results
            setSearchResults([]);
            return;
        }

        // Translate Hebrew food names to English using the foodMap
        let translatedTerm = term;
        Object.keys(foodMap).forEach((hebrewFood) => {
            if (translatedTerm.includes(hebrewFood)) {
                translatedTerm = translatedTerm.replace(hebrewFood, foodMap[hebrewFood]);
            }
        });

        // Proceed with the API request using the translated search term
        setLoading(true);
        setError("");
        setSearchResults([]);

        try {
            const data = await fetchData(
                "https://api.nal.usda.gov/fdc/v1/foods/search",
                foodOptions,
                { api_key: process.env.REACT_APP_FOOD_API_KEY, query: translatedTerm }
            );
            setSearchResults(data.foods || []);
        } catch (err) {
            console.error("Error fetching food data:", err);
            setError("Failed to fetch food data. Please try again.");
        } finally {
            setLoading(false);
        }
    }, 500); // 500ms debounce

    useEffect(() => {
        handleSearchDebounced(searchTerm);
        return handleSearchDebounced.cancel; // Cleanup debounce on unmount
    }, [searchTerm]);

    const handleTimePeriodChange = (period) => {
        setTimePeriod(period);
        fetchCaloriesData(period);
    };
    const activeStyle = {
        cursor: 'pointer',
        fontWeight: 'bold',
        color: '#d5011e', // A highlight color when selected
    };

    const inactiveStyle = {
        cursor: 'pointer',
        color: '#333', // Default color for unselected
        textDecoration: 'none',
    };

    const fetchCaloriesData = async (period) => {
        try {
            const meals = await getMeal(userId);
            const calorieData = {};

            meals.forEach((meal) => {
                const date = new Date(meal.date);
                const calories = meal.foodNutrients?.find((n) => n.nutrientName === "Energy")?.value || 0;

                let key;
                switch (period) {
                    case 'weekly':
                        const week = `${date.getFullYear()}-W${Math.ceil((date.getDate() + 1 - date.getDay()) / 7)}`;
                        key = week;
                        break;
                    case 'monthly':
                        key = `${date.getFullYear()}-${date.getMonth() + 1}`; // "YYYY-MM"
                        break;
                    case 'yearly':
                        key = `${date.getFullYear()}`; // "YYYY"
                        break;
                    default:
                        key = date.toLocaleDateString(); // Default to daily
                }

                calorieData[key] = (calorieData[key] || 0) + calories;
            });

            const chartData = {
                labels: Object.keys(calorieData),
                datasets: [
                    {
                        label: "Total Calories",
                        data: Object.values(calorieData),
                        backgroundColor: "#C4A484",
                        borderColor: "#C4A484",
                        borderWidth: 1,
                        fill: true,
                    },
                ],
            };

            setChartData((prev) => ({
                ...prev,
                dailyCaloriesData: chartData,
            }));
        } catch (error) {
            console.error("Error fetching calorie data:", error);
        }
    };

    // Initially fetch the daily data
    useEffect(() => {
        fetchCaloriesData('daily');
    }, []);

    const fetchChartData = async () => {
        try {
            const foods = await getMeal(userId);

            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

            // Filter foods to only include meals from today (00:00 to 23:59)
            const todayFoods = foods.filter(food => {
                const mealDate = new Date(food.date);
                return mealDate >= startOfDay && mealDate <= endOfDay;
            });
            const mealCalories = {};
            const groupedMeals = {};
            const nutrientTotals = {}; // To store totals for all nutrients
            const nutrientGoalsTemp = {};

            todayFoods.forEach((food) => {
                const meal = food.mealType;
                const calories = food.foodNutrients?.find((n) => n.nutrientName === "Energy")?.value || 0;
                mealCalories[meal] = (mealCalories[meal] || 0) + calories;
                groupedMeals[meal] = groupedMeals[meal] || [];
                groupedMeals[meal].push(food);

                food.foodNutrients.forEach((nutrient) => {
                    const name = nutrient.nutrientName;
                    if (!nutrientTotals[name]) nutrientTotals[name] = 0;
                    nutrientTotals[name] += nutrient.value;


                });

            });

            setMealDetails(groupedMeals);
            setNutrientGoals(nutrientTotals);
            setNutrientGoals((prev) => ({ ...prev, ...nutrientGoalsTemp }));

            const data = {
                labels: Object.keys(mealCalories),
                datasets: [
                    {
                        data: Object.values(mealCalories),
                        backgroundColor: ["#A3C9A8", "#EDE0D4", "#C4A484", "#B5A78A"],
                    },
                ],
            };
            setChartData(data);
        } catch (error) {
            console.error("Error fetching chart data:", error);
        }
    };


    const handleOpenChart = async () => {
        await fetchChartData();
        await fetchCaloriesData(); // Fetch daily calorie data
        setChartOpen(true);
    };

    const renderNutrientGoals = () => {
        const energyValue = nutrientGoals["Energy"]; // Extract the energy (calories) from the nutrientGoals
        const energyGoal = nutrientGoalValues["Energy"] || 1000; // Use a default goal if no energy goal exists.

        return (
            <Box>
                {/* Energy (Calories) Display */}
                <Typography fontFamily="GveretLevin" variant="h6" diretion="rtl" textAlign="right" mb={2} color="#B5A78A" mr={1}>
                    יעד קלורי יומי
                </Typography>
                <Box mb={2}>
                    <Typography variant="body1">
                        Calories: {energyValue ? energyValue.toFixed(2) : 0} / {energyGoal} kcal
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        sx={{
                            backgroundColor: "#f5e1c8", // Change track color (background)
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "#A3C9A8" // Change progress bar color
                            }
                        }}
                        value={Math.min(((energyValue || 0) / energyGoal) * 100, 100)} // Cap the value at 100
                    />
                </Box>

                {/* Other Nutrients Display */}
                <Typography fontFamily="GveretLevin" variant="h6" diretion="rtl" textAlign="right" mb={2} color="#B5A78A" mr={1}>
                    ערכים תזונתיים
                </Typography>
                {Object.keys(nutrientGoals).map((nutrient) => {
                    if (nutrient === "Energy") return null; // Skip energy since it's already displayed above.

                    // Check if the goal value exists, otherwise use the default value (100)
                    const goalValue = nutrientGoalValues[nutrient] || 100;
                    const nutrientGoal = nutrientGoals[nutrient] || 0; // Default to 0 if no goal value is available

                    return (
                        <Box key={nutrient} mb={2}>
                            <Tooltip title={nutrient.charAt(0).toUpperCase() + nutrient.slice(1)} arrow>
                                <Typography variant="body1">
                                    {getTruncatedNutrientName(nutrient)}:{" "}
                                    {nutrientGoal.toFixed(2)} / {goalValue}{" "}
                                    {nutrientGoals[nutrient]?.unitName || "g"}
                                </Typography>
                            </Tooltip>
                            <LinearProgress
                                variant="determinate"
                                sx={{
                                    backgroundColor: "#f5e1c8", // Change track color (background)
                                    "& .MuiLinearProgress-bar": {
                                        backgroundColor: "#A3C9A8" // Change progress bar color
                                    }
                                }}
                                value={Math.min((nutrientGoal / goalValue) * 100, 100)} // Cap the value at 100
                            />
                        </Box>
                    );
                })}
            </Box>
        );
    };



    const renderMealDetails = () => (
        <Box>
            {Object.entries(mealDetails).map(([mealType, meals]) => (
                <Box key={mealType} mb={3}>
                    <Typography
                        fontFamily="GveretLevin"
                        variant="h6"
                        mb={1}
                        color="#B5A78A"
                        ml={1}
                        direction="rtl"
                        textAlign="right"
                    >
                        {mealTypes[mealType.charAt(0).toUpperCase() + mealType.slice(1)]}
                    </Typography>
                    {meals.map((meal, index) => (
                        <Tooltip
                            key={index}
                            title={
                                <Box>
                                    {meal.foodNutrients
                                        ?.filter((nutrient) => nutrient.value > 0)
                                        .map((nutrient) => (
                                            <Typography key={nutrient.nutrientId} variant="body2">
                                                {nutrient.nutrientName}: {nutrient.value} {nutrient.unitName}
                                            </Typography>
                                        ))}
                                </Box>
                            }
                            arrow
                            placement="top"
                        >
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    boxShadow: 2,
                                    borderRadius: 2,
                                    backgroundColor: "white",
                                    cursor: "pointer", // Make it clear it's interactable
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",

                                    }}
                                >
                                    <Typography color="#B5A78A" variant="h5" sx={{ fontFamily: 'GveretLevin' }}>
                                        {meal.description.toLowerCase()}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        diretion: "rtl", textAlign: "right"
                                    }}
                                >
                                    <IconButton onClick={() => handleRemoveMeal(meal._id)}>
                                        <RemoveCircle sx={{
                                            "& .MuiLinearProgress-bar": {
                                                backgroundColor: "#4CAF50" // Change progress bar color
                                            }
                                        }} />
                                    </IconButton>
                                    <Typography diretion="rtl" textAlign="right" variant="body2" justifyContent="space-between"
                                    >
                                        קלוריות: {meal.foodNutrients?.find((n) => n.nutrientName === "Energy")?.value || "N/A"}

                                    </Typography>

                                </Box>
                                <Box>
                                    <Typography variant="body2" alignItems="right" diretion="rtl" textAlign="right" >
                                        גרם:{" "}
                                        {meal.weight || "N/A"}
                                    </Typography>
                                </Box>

                                {/* Tooltip for Ingredients */}
                                <Tooltip
                                    title={meal.ingredients.toLowerCase() || "No ingredients available"}
                                    arrow
                                    placement="top"
                                >
                                    <Typography
                                        alignItems="right"
                                        direction="rtl"
                                        textAlign="right"
                                        variant="body2"
                                    >
                                        מרכיבים :{" "}
                                        {meal.ingredients.toLowerCase() ? (
                                            <Typography
                                                component="span"
                                                variant="body2" // or use variant="Arial" if you have a custom variant defined
                                                sx={{
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                    color: "primary.main",
                                                    fontFamily: "Arial" // if you want to force Arial
                                                }}
                                            >
                                                הצג
                                            </Typography>
                                        ) : (
                                            "N/A"
                                        )}
                                    </Typography>

                                </Tooltip>
                            </Box>
                        </Tooltip>
                    ))
                    }
                </Box >
            ))
            }
        </Box >
    );



    const handleTabChange = (event, newIndex) => {
        setTabIndex(newIndex);
    };
    const handleAddMeal = (food) => {
        setSelectedFood(food);
        setMealTypeDialogOpen(true);

    };



    // Helper function to always extract weight in grams
    const getWeightInGrams = (food) => {
        if (!food) return 0;

        // If there is a dedicated property for grams, use it.
        if (food.packageWeightInGrams) {
            return parseFloat(food.packageWeightInGrams);
        }

        // If packageWeight is a string that may include both grams and oz,
        // attempt to extract the grams from the string (e.g., "300 g / 10.6 oz")
        if (typeof food.packageWeight === "string") {
            const gramMatch = food.packageWeight.match(/(\d+(?:\.\d+)?)\s*g/i);
            if (gramMatch) {
                return parseFloat(gramMatch[1]);
            }
            // Fallback: try to parse the string as a number
            return parseFloat(food.packageWeight) || 0;
        }

        // Otherwise, assume it's a number representing grams.
        return food.packageWeight || 0;
    };

    const handleConfirmAddMeal = async (mealType) => {
        if (!selectedFood) return;

        // Always get the weight in grams
        const originalWeight = getWeightInGrams(selectedFood);
        let multiplier = 1; // Default multiplier (1 means no change)

        // Calculate the multiplier only if a value was entered and we have a valid original weight
        if (servingSizeValue && originalWeight > 0) {
            if (servingSizeType === "grams") {
                // For grams, compute the multiplier as (entered grams / original grams)
                multiplier = parseFloat(servingSizeValue) / originalWeight;
            } else if (servingSizeType === "percentage") {
                // For percentage, the entered value is assumed to be percent (e.g., 67 means 67%)
                multiplier = parseFloat(servingSizeValue) / 100;
            }
        }

        // Calculate the adjusted weight (in grams) using the multiplier
        const adjustedWeight = parseFloat((originalWeight * multiplier).toFixed(2));

        // Adjust each nutrient value proportionally using the same multiplier.
        const adjustedNutrients =
            selectedFood?.foodNutrients?.map((nutrient) => ({
                ...nutrient,
                value: (nutrient.value * multiplier).toFixed(2),
            })) || [];

        // Build the food object using the adjusted weight and nutrient values.
        const food = {
            description: selectedFood?.description || "Unknown",
            brandOwner: selectedFood?.brandOwner || "N/A",
            weight: adjustedWeight, // The final serving weight (in grams)
            calories:
                ((selectedFood?.foodNutrients?.find(
                    (nutrient) => nutrient.nutrientName === "Energy"
                )?.value || 0) * multiplier).toFixed(2),
            ingredients: selectedFood?.ingredients || "N/A",
            foodNutrients: adjustedNutrients,
            mealType: mealType.toLowerCase(),
        };

        try {
            // Save the food item as a meal (by calling your addMeal API)
            await addMeal(userId, food, food.mealType);
            setShowPopup(true); // popup
            window.location.reload(); // Refresh the page to show the new meal
        } catch (error) {
            console.error("Error adding meal:", error);
        } finally {
            // Reset the dialogs and serving size states after adding the meal
            setMealTypeDialogOpen(false);
            setSelectedFood(null);
            setServingSizeValue("");
            setServingSizeType("grams");
        }
    };



    const handleRemoveMeal = async (mealId) => {
        try {
            await deleteMeal(userId, mealId); // Call API to remove the meal
            fetchChartData(); // Refresh the data after deletion
            fetchCaloriesData();
        } catch (error) {
            console.error("Error deleting meal:", error);
        }
    };

    const getTruncatedNutrientName = (nutrient) => {
        if (nutrient.includes(',') || nutrient.includes('(')) {
            // Split the string by space to get the first two words
            const words = nutrient.split(/,|\(/);;
            // Truncate and add '...' after the first two words
            return `${words[0]}  ...`;
        }
        // Return full name if no ',' or '(' is found
        return nutrient.charAt(0).toUpperCase() + nutrient.slice(1);
    };

    return (
        <Box minHeight="500px" sx={{ mt: { lg: "96px", xs: "60px" }, px: { lg: "20px", xs: "10px" } }}>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 4,
                }}
            >
                <IconButton
                    sx={{
                        backgroundColor: "#B5A78A",
                        color: "white",
                        "&:hover": { backgroundColor: "#B5A78A" },
                        borderRadius: "50%",
                    }}
                    onClick={handleOpenChart}
                >
                    <InsertChart />
                </IconButton>
                <Typography fontFamily="GveretLevin" variant="h4" marginLeft={'4px'} >חיפוש מוצרים</Typography>

            </Box>

            <Box
                sx={{
                    display: "center",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 4,
                }}
            >
                <TextField
                    variant="outlined"
                    placeholder="חפש מוצר"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        flex: 1,
                        mr: 2,
                        '& .MuiInputBase-input': {
                            textAlign: 'center',
                        },
                    }}
                />


            </Box>

            {loading && <Loader />}
            {error && <Typography sx={{
                backgroundColor: "#f5e1c8", // Change track color (background)
                "& .MuiLinearProgress-bar": {
                    backgroundColor: "#4CAF50" // Change progress bar color
                }
            }} >{error}</Typography>}

            {
                searchResults.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",  // Change to column to stack items vertically
                            alignItems: "center",     // Center the items horizontally
                            gap: 2,                   // Add space between items
                        }}
                    >
                        {searchResults.map((food) => (
                            <Tooltip
                                key={food.fdcId}
                                title={
                                    <Box>
                                        {food.foodNutrients
                                            ?.filter((nutrient) => nutrient.value > 0)
                                            .map((nutrient) => (
                                                <Typography
                                                    key={nutrient.nutrientId}
                                                    variant="body2"
                                                >
                                                    {nutrient.nutrientName}: {nutrient.value}{" "}
                                                    {nutrient.unitName}
                                                </Typography>
                                            ))}
                                    </Box>
                                }
                                arrow
                            >
                                <Box
                                    sx={{
                                        width: "80%",   // Make the Box take full width of its parent
                                        padding: "10px",
                                        border: "1px solid #ccc",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <IconButton onClick={() => handleAddMeal(food)}>
                                        <AddCircle color="#C4A484" />
                                    </IconButton>
                                    <Box >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "#C4A484",
                                                fontFamily: 'GveretLevin',
                                                direction: 'ltr',
                                            }}

                                        >
                                            {food.description}
                                        </Typography>
                                        <Typography direction="rtl" textAlign="right" variant="body2" color="text.secondary">
                                            {food.brandOwner || "N/A"} :חברה


                                        </Typography>

                                        <Typography direction="rtl" textAlign="right" variant="body2" color="text.secondary">
                                            קלוריות:{" "}
                                            {food.foodNutrients?.find(
                                                (nutrient) => nutrient.nutrientName === "Energy"
                                            )?.value || "N/A"}{" "}
                                            קל
                                        </Typography>
                                        <Typography direction="rtl" textAlign="right" variant="body2" color="text.secondary">
                                            {food.packageWeight || "N/A"}  :גרם
                                        </Typography>
                                        <Typography direction="rtl" textAlign="right" variant="body2" color="text.secondary">
                                            מרכיבים:{""}
                                            <Tooltip title={food.ingredients?.toLowerCase() || "N/A"} arrow placement="top">
                                                <Typography
                                                    component="span"
                                                    variant="Arial"
                                                    sx={{
                                                        textDecoration: "underline",
                                                        cursor: "pointer",
                                                        color: "primary.main",

                                                    }}
                                                >
                                                    הצג
                                                </Typography>
                                            </Tooltip>
                                        </Typography>
                                    </Box>

                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                )
            }


            <Dialog
                z-index="1000"
                open={chartOpen}
                onClose={() => setChartOpen(false)}
                fullWidth
                maxWidth="md"
                style={{ overflowY: 'visible' }}
            >
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    centered={false}
                    TabIndicatorProps={{
                        style: { backgroundColor: '#C4A484' }, // Change indicator color to red
                    }}
                >
                    <Tab label="גרף" sx={{
                        color: tabIndex === 0 ? '#C4A484' : 'inherit', // Set active tab text to red
                        '&.Mui-selected': { color: '#C4A484' }, // Ensure selected text color is red
                    }} />
                    <Tab label="מעקב יומי" sx={{
                        color: tabIndex === 1 ? '#C4A484' : 'inherit', // Set active tab text to red
                        '&.Mui-selected': { color: '#C4A484' }, // Ensure selected text color is red
                    }} />
                    <Tab label="ערכים תזונתיים" sx={{
                        color: tabIndex === 2 ? '#C4A484' : 'inherit', // Set active tab text to red
                        '&.Mui-selected': { color: '#C4A484' }, // Ensure selected text color is red
                    }} />
                    <Tab label="יעד קלורי " sx={{
                        color: tabIndex === 3 ? '#C4A484' : 'inherit', // Set active tab text to red
                        '&.Mui-selected': { color: '#C4A484' }, // Ensure selected text color is red
                    }} />
                </Tabs>
                <Box
                    display="flex"
                    justifyContent="center"
                    mt={2}
                    style={{
                        position: 'relative', // Ensure it stays below the content
                        marginTop: '16px',    // Provide space from the content
                    }}
                >
                    {[0, 1, 2, 3].map((index) => (
                        <Box
                            key={index}
                            onClick={() => handleTabChange(null, index)}
                            sx={{
                                width: 6, // Dot size
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: tabIndex === index ? '#C4A484' : '#ccc', // Highlight active dot
                                margin: '0 8px',
                                cursor: 'pointer',
                            }}
                        />
                    ))}
                </Box>
                <Box p={2} style={{ boxSizing: 'border-box', overflow: 'auto' }}>

                    {tabIndex === 0 && chartData && (
                        <Box>
                            <Pie data={chartData} />

                            <Line
                                data={chartData.dailyCaloriesData}
                                options={{
                                    responsive: true,
                                    plugins: {
                                        legend: {
                                            position: "top",
                                        },
                                    },
                                    scales: {
                                        x: { display: true },
                                        y: {},
                                    },
                                }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '10px' }}>
                                <span
                                    onClick={() => handleTimePeriodChange('daily')}
                                    style={timePeriod === 'daily' ? activeStyle : inactiveStyle}
                                >
                                    יומי
                                </span>
                                <span
                                    onClick={() => handleTimePeriodChange('weekly')}
                                    style={timePeriod === 'weekly' ? activeStyle : inactiveStyle}
                                >
                                    שבועי
                                </span>
                                <span
                                    onClick={() => handleTimePeriodChange('monthly')}
                                    style={timePeriod === 'monthly' ? activeStyle : inactiveStyle}
                                >
                                    חודשי
                                </span>
                                <span
                                    onClick={() => handleTimePeriodChange('yearly')}
                                    style={timePeriod === 'yearly' ? activeStyle : inactiveStyle}
                                >
                                    שנתי
                                </span>
                            </div>

                        </Box>
                    )}
                    {tabIndex === 1 && renderMealDetails()}
                    {tabIndex === 2 && renderNutrientGoals()}
                    {tabIndex === 3 && (
                        <CaloricGoal
                            userId={userId}
                            onUpdateGoal={updateCaloricGoal}
                            onClose={() => setIsGoalModalOpen(false)}
                        />
                    )}
                </Box>

            </Dialog>

            <Dialog open={mealTypeDialogOpen} onClose={() => setMealTypeDialogOpen(false)}>
                <Box p={4} sx={{ textAlign: "center" }}>
                    <Typography direction="rtl" fontFamily="GveretLevin" variant="h6" mb={2}>
                        :הוספת המנה לארוחה
                    </Typography>

                    <Box sx={{ direction: 'rtl', display: "flex", flexDirection: "column", gap: 2 }}>


                        {Object.entries(mealTypes).map(([english, hebrew]) => (
                            <Button
                                key={english}
                                variant="contained"
                                sx={{
                                    backgroundColor: "#C4A484",
                                    "&:hover": { backgroundColor: "#C4A484" },
                                }}
                                onClick={() => handleConfirmAddMeal(english)}
                            >
                                {hebrew}
                            </Button>
                        ))}

                        {/* Button to open the serving size editing modal */}
                        <span
                            variant="outlined"

                            onClick={() => setServingSizeDialogOpen(true)}
                            style={{
                                fontSize: '0.9em',
                                color: '#007bff',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                alignItems: 'right',
                                justifyContent: 'right',
                                fontFamily: 'GveretLevin',
                            }}
                        >
                            ערוך גודל מנה
                        </span>
                    </Box>
                </Box>
            </Dialog>
            {showPopup && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                    onClick={() => setShowPopup(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#FFF',
                            padding: '40px',
                            borderRadius: '10px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            maxWidth: '400px',
                            width: '70%'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <span style={{ fontSize: '48px' }}>👍</span>
                        </div>
                        <p style={{ fontSize: '24px', fontWeight: '700', textAlign: 'center', marginBottom: '20px' }}>
                            !המנה נוספה
                        </p>

                        <button
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#B5A78A',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onClick={() => setShowPopup(false)}
                        >
                            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginRight: '5px' }}>!</span>
                            <MonetizationOnIcon sx={{ fontSize: '20px', color: '#FFD700' }} />
                            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginRight: '5px' }}>3 קיבלת</span>
                        </button>
                    </div>
                </div>
            )
            }
            {/* Serving Size Editing Dialog */}
            <Dialog
                open={servingSizeDialogOpen}
                onClose={() => setServingSizeDialogOpen(false)}
            >
                <Box p={4} dir="rtl" sx={{ fontFamily: 'GveretLevin' }}
                >
                    <Typography variant="h6" mb={2} fontFamily="GveretLevin">
                        ערוך גודל מנה
                    </Typography>
                    <FormControl component="fieldset" diretion="rtl" textAlign="right" mb={2}>
                        <RadioGroup
                            row
                            value={servingSizeType}
                            onChange={(e) => setServingSizeType(e.target.value)}

                        >
                            <FormControlLabel
                                value="grams"
                                control={<Radio />}
                                label="גרמים"
                            />
                            <FormControlLabel
                                value="percentage"
                                control={<Radio />}
                                label="אחוזים"

                            />
                        </RadioGroup>
                    </FormControl>
                    <Box fontFamily="GveretLevin" sx={{ display: "flex", flexDirection: "column" }}>
                        <TextField
                            fontFamily="GveretLevin"
                            label={
                                servingSizeType === "grams"
                                    ? "משקל בגרמים"
                                    : "אחוז מהמנה (100 = מלא)"
                            }
                            value={servingSizeValue}
                            onChange={(e) => setServingSizeValue(e.target.value)}
                            type="number"
                            margin="normal"
                            InputLabelProps={{
                                sx: {
                                    textAlign: "left",
                                    width: "100%", // Ensures the label takes up full width
                                    fontFamily: 'GveretLevin'

                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={() => setServingSizeDialogOpen(false)}
                            sx={{
                                backgroundColor: "#C4A484",
                                "&:hover": { backgroundColor: "#C4A484" },
                                fontFamily: 'GveretLevin'

                            }}
                        >
                            שמור
                        </Button>
                    </Box>


                </Box>

            </Dialog >



        </Box >
    );
};

export default FoodTrack; 